import { Button } from '@/app/common/components/button/Button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card.tsx'
import Pagination from '@/common/components/Pagination.tsx'
import SearchInput from '@/common/components/SearchInput.tsx'
import { CircleArrowsIcon } from '@/common/icons/arrows.tsx'
import { cn, currencyFormatter } from '@/common/utilities.ts'
import { formatDateToUsersTimezone } from '@/lib/utils'
import { AppRoutePaths } from '@/routing/route_paths'
import { RootState } from '@/storage/store'
import { useQuery } from '@tanstack/react-query'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { DownIcon, UpIcon } from '@/common/icons/general.tsx'
import { SubmitButton } from '@/common/components/SubmitButton.tsx'
import { useToast } from '@/shad/ui/toast/use-toast'
import { useExportTransactions } from '@/queries/transactions/useExportTransactions.ts'
import { useExportSpendReport } from '@/queries/transactions/useExportSpendReport.ts'
import TransactionStatusBadge from '@/app/common/components/TransactionStatusBadge'
import { TransactionStatusSchema } from '@/schemas/TransactionStatusSchema'
import DatePickerWithRange from '@/common/components/DateRangePicker'
import { DateRange } from 'react-day-picker'
import {
  ExportTransactionsParams,
  getPaginatedTransactions,
  type TransactionSortBy,
} from '@/queries/transactions/queries.ts'
const columnHelper = createColumnHelper<ITransaction>()

export type ITransaction = {
  transaction_id: number
  applicant_name: string
  student_name: string[]
  transaction_type: string
  provider_id: number | null
  provider_name: string | null
  status: number
  created_datetime: string
  invoice_quote: string
  total_amount: number
  uuid: string | null
}

type TransactionSortableColumnLabel =
  | 'ID'
  | 'Applicant Name'
  | 'Student Name'
  | 'Type'
  | 'Provider'
  | 'Invoice No.'
  | 'Total Amount'
  | 'Transaction UUID'
  | 'Status'
  | 'Created At'

const columnLabelValDic: Record<
  TransactionSortableColumnLabel,
  TransactionSortBy
> = {
  ID: 'transaction_id',
  'Applicant Name': 'applicant_name',
  'Student Name': 'student_name',
  Type: 'transaction_type',
  Provider: 'provider_name',
  'Invoice No.': 'invoice_quote',
  'Total Amount': 'total_amount',
  'Transaction UUID': 'uuid',
  Status: 'status',
  'Created At': 'created_datetime',
}

const RECORDS_PER_PAGE = 10 as const

function headerForColumn(
  name: TransactionSortableColumnLabel,
  sort_by: TransactionSortBy,
  sort_order: 'asc' | 'desc',
  onSort: (column: TransactionSortableColumnLabel) => void
) {
  const colVar = columnLabelValDic[name]
  const isActiveSort = colVar === sort_by

  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 bg-transparent p-0 text-left"
      onClick={() => onSort(name)}
      aria-label={`Sort by ${name}`}
    >
      {name}
      <span className="inline-flex flex-col items-center leading-none">
        <UpIcon
          className={cn(
            'text-xs leading-none',
            isActiveSort && sort_order === 'asc'
              ? 'text-gray-900'
              : 'text-gray-400'
          )}
        />
        <DownIcon
          className={cn(
            '-mt-1 text-xs leading-none',
            isActiveSort && sort_order === 'desc'
              ? 'text-gray-900'
              : 'text-gray-400'
          )}
        />
      </span>
    </button>
  )
}

export default function TransactionsPage() {
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setSearch] = useState('')
  const [recordsPerPage, setRecordsPerPage] = useState<number>(RECORDS_PER_PAGE)
  const [sort_by, setSortBy] = useState<TransactionSortBy>('created_datetime')
  const [sort_order, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [dateRange, setDateRange] = useState<DateRange | undefined>()
  const navigate = useNavigate()
  const programId = useSelector(
    (s: RootState) => s.programs.selectedProgramYear?.id
  ) as number
  const { toast } = useToast()

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [
      'transactions',
      currentPage,
      recordsPerPage,
      search,
      programId,
      sort_by,
      sort_order,
      dateRange?.from,
      dateRange?.to,
    ],
    queryFn: () =>
      getPaginatedTransactions(
        programId,
        currentPage,
        recordsPerPage,
        2,
        'en',
        search,
        sort_by,
        sort_order,
        dateRange?.from
          ? { startDate: dateRange.from, endDate: dateRange.to }
          : undefined
      ),
    refetchOnMount: 'always',
  })

  useEffect(() => {
    setCurrentPage(1)
  }, [search, dateRange?.from, dateRange?.to])

  const transactions: ITransaction[] = data?.data || [] // Use empty array as fallback
  const totalRows = data?.total || 0
  const currentPageNumber = data?.current_page || 1

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerPageChange = (perPage: number) => {
    setRecordsPerPage(perPage)
  }

  const sortBy = (column: TransactionSortableColumnLabel) => {
    const colVar = columnLabelValDic[column]
    const newOrder =
      colVar === sort_by ? (sort_order === 'desc' ? 'asc' : 'desc') : 'asc'

    setCurrentPage(1)
    setSortBy(colVar)
    setSortOrder(newOrder)
  }

  const downloadBlob = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const getExportParams = (programId: number): ExportTransactionsParams => {
    const trimmedSearch = search.trim()

    return dateRange?.from
      ? {
          programId,
          ...(trimmedSearch && { search: trimmedSearch }),
          startDate: dateRange.from,
          endDate: dateRange.to,
        }
      : {
          programId,
          ...(trimmedSearch && { search: trimmedSearch }),
        }
  }

  const exportTransactionsMutation = useExportTransactions({
    onSuccess: (blob) => {
      downloadBlob(blob, 'transactions.csv')

      toast({
        title: 'Transactions list has been exported successfully.',
        variant: 'default',
      })
    },
    onError: (error) => {
      toast({
        title: 'Failed to export transactions',
        description: error.message || 'Unable to export transactions.',
        variant: 'destructive',
      })
    },
  })

  const handleExportTransactions = () => {
    if (!programId || exportTransactionsMutation.isPending) {
      return
    }

    exportTransactionsMutation.mutate(getExportParams(programId))
  }

  const exportSpendReportMutation = useExportSpendReport({
    onSuccess: (blob) => {
      downloadBlob(blob, 'spend_report.csv')

      toast({
        title: 'Spend report has been exported successfully.',
        variant: 'default',
      })
    },
    onError: (error) => {
      toast({
        title: 'Failed to export spend report',
        description: error.message || 'Unable to export spend report.',
        variant: 'destructive',
      })
    },
  })

  const handleExportSpendReport = () => {
    if (!programId || exportSpendReportMutation.isPending) {
      return
    }

    exportSpendReportMutation.mutate(getExportParams(programId))
  }

  const columns = [
    columnHelper.accessor('transaction_id', {
      id: 'transaction_id',
      header: () => headerForColumn('ID', sort_by, sort_order, sortBy),
      cell: (info) => (
        <div className="font-medium text-gray-800 ">{info.getValue()}</div>
      ),
    }),
    columnHelper.accessor('applicant_name', {
      id: 'applicant_name',
      header: () =>
        headerForColumn('Applicant Name', sort_by, sort_order, sortBy),
      cell: (info) => <div className="">{info.getValue()}</div>,
    }),
    columnHelper.accessor('student_name', {
      id: 'student_name',
      header: () =>
        headerForColumn('Student Name', sort_by, sort_order, sortBy),
      cell: (info) => <div className=" ">{info.getValue().join(', ')}</div>,
    }),
    columnHelper.accessor('transaction_type', {
      id: 'transaction_type',
      header: () => headerForColumn('Type', sort_by, sort_order, sortBy),
      cell: (info) => <div>{String(info.getValue())}</div>,
    }),
    columnHelper.accessor('provider_name', {
      id: 'provider',
      header: () => headerForColumn('Provider', sort_by, sort_order, sortBy),
      cell: (info) => {
        const row = info.row.original

        return (
          <div>
            {row.provider_name
              ? row.provider_id !== null
                ? `${row.provider_name} (ID: ${row.provider_id})`
                : row.provider_name
              : ''}
          </div>
        )
      },
    }),
    columnHelper.accessor('invoice_quote', {
      id: 'invoice_quote',
      header: () => headerForColumn('Invoice No.', sort_by, sort_order, sortBy),
      cell: (info) => (
        <div className="max-w-[5rem] truncate overflow-hidden">
          {info.getValue() && `${info.getValue()}`}
        </div>
      ),
    }),
    columnHelper.accessor('total_amount', {
      id: 'total_amount',
      header: () =>
        headerForColumn('Total Amount', sort_by, sort_order, sortBy),
      cell: (info) => <div>{currencyFormatter.format(info.getValue())}</div>,
    }),
    columnHelper.accessor('uuid', {
      id: 'uuid',
      header: () =>
        headerForColumn('Transaction UUID', sort_by, sort_order, sortBy),
      cell: (info) => {
        const value = info.getValue()
        return (
          <div
            className="max-w-[5rem] truncate overflow-hidden"
            title={value ?? ''}
          >
            {value}
          </div>
        )
      },
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => headerForColumn('Status', sort_by, sort_order, sortBy),
      cell: (info) => {
        const status = TransactionStatusSchema.parse(info.getValue())
        return <TransactionStatusBadge value={status} />
      },
    }),
    columnHelper.accessor('created_datetime', {
      id: 'created_datetime',
      header: () => headerForColumn('Created At', sort_by, sort_order, sortBy),
      cell: (info) => {
        const date = info.getValue()
        return <div>{formatDateToUsersTimezone(date)}</div>
      },
    }),
    columnHelper.display({
      id: 'action',
      header: () => <span className="pr-2 2xl:p-1">Action</span>,
      cell: (info) => {
        const row = info.row.original
        return (
          <Button
            onClick={() => {
              navigate(
                AppRoutePaths.transactions.details.index.replace(
                  ':transactionId',
                  row.transaction_id.toString()
                )
              )
            }}
          >
            Review
          </Button>
        )
      },
    }),
  ]

  const table = useReactTable({
    data: transactions,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: Math.ceil(totalRows / recordsPerPage) || 1,
  })

  return (
    <>
      <section aria-labelledby="transactions-list-title">
        <Card className="mt-6">
          <CardHeader className="flex justify-between items-center text-md font-semibold">
            <div className="text-left text-lg md:text-md text-gray-900">
              Transactions
              <span className="hidden mt-1 text-sm font-normal text-gray-400 lg-sm:block lg-sm:w-full">
                {'last updated at: 06:05:32 PM'}
              </span>
            </div>
            <div>
              <div className="space-y-3 flex flex-col items-start lg-sm:flex-row lg-sm:items-center lg-sm:space-y-0 lg-sm:space-x-4">
                <DatePickerWithRange
                  value={dateRange}
                  onDateChange={setDateRange}
                  label="Filter By Date"
                />
                <SearchInput
                  id="table_search"
                  placeholder="Search Transactions"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <SubmitButton
                  type="button"
                  variant="primary"
                  size="md"
                  className="w-32 whitespace-nowrap"
                  status={exportTransactionsMutation.status}
                  buttonTxt="Export"
                  submittingText="Exporting..."
                  forceDisabled={!programId}
                  onClick={handleExportTransactions}
                />
                <SubmitButton
                  type="button"
                  variant="outline"
                  size="md"
                  className="w-40 whitespace-nowrap"
                  status={exportSpendReportMutation.status}
                  buttonTxt="Export Spend Report"
                  submittingText="Exporting..."
                  forceDisabled={!programId}
                  onClick={handleExportSpendReport}
                />
                <div className="flex space-x-2 lg-sm:flex-col lg-sm:space-x-0">
                  <Button
                    variant="outline"
                    className="max-w-12"
                    size="md"
                    disabled={isLoading}
                    onClick={() => refetch()}
                  >
                    <CircleArrowsIcon className="text-base" />
                  </Button>
                  <span className="block w-15ch text-sm font-normal text-gray-400 lg-sm:w-full lg-sm:hidden">
                    {'last updated at: 06:05:32 PM'}
                  </span>
                </div>
              </div>
            </div>
          </CardHeader>
          {isLoading ? (
            <CardContent>Loading...</CardContent>
          ) : isError ? (
            <CardContent>Error fetching transactions.</CardContent>
          ) : (
            <CardContent className="flex flex-col p-0 lg:p-0 h-full w-full">
              <table>
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header, index) => (
                        <th
                          key={header.id}
                          className={cn(
                            'px-2 py-3.5 text-left text-sm font-semibold text-gray-900 md:py-4 md:px-2.5',
                            index != 0 &&
                              index != headerGroup.headers.length - 1 &&
                              'hidden md:table-cell',
                            index == headerGroup.headers.length - 1 &&
                              'text-left'
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell, index) => (
                        <td
                          key={cell.id}
                          className={cn(
                            ' py-4 px-2 text-sm font-sans text-left text-gray-500 border-t border-gray-200 md:py-4 md:px-2',
                            index != 0 &&
                              index != row.getVisibleCells().length - 1 &&
                              'hidden md:table-cell',
                            index == row.getVisibleCells().length - 1 &&
                              'text-left'
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          )}
          <CardFooter className="p-0 border-t border-transparent mt-5">
            <Pagination
              itemsPerPage={recordsPerPage}
              currentPage={currentPageNumber}
              totalRows={totalRows}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
          </CardFooter>
        </Card>
      </section>
    </>
  )
}
